import { LinkBubbleMenuHandler } from 'mui-tiptap'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import Color from '@tiptap/extension-color'
import { TextStyle, FontSize } from '@tiptap/extension-text-style'
import Paragraph from '@tiptap/extension-paragraph'
import { Extension } from '@tiptap/core'
import BulletList from '@tiptap/extension-bullet-list'

import PageBreak from '@/modules/base/components/inputs/html-input/extensions/page-break'
import EnhancedOrderedList from '@/modules/base/components/inputs/html-input/extensions/enhanced-ordered-list/enhanced-ordered-list'
import { PlaceholderNode } from '@/modules/base/components/inputs/html-input/extensions/placeholder'
import { MjmlButtonNode } from '@/modules/base/components/inputs/html-input/extensions/mjml-button'
import { ButtonItem } from '@/modules/base/components/inputs/html-input/extensions/mjml-button/types'
import { EnhancedHighlight } from '@/modules/base/components/inputs/html-input/extensions/enhanced-highlight'
import { preserveWhitespaceInHtml } from '@/modules/base/components/inputs/html-input/_helpers/formatHelpers'

export type FormatOption =
| 'bold'
| 'italic'
| 'underline'
| 'fontSize'
| 'fontColor'
| 'fontBackgroundColor'
| 'undo'
| 'redo'
| 'alignment'
| 'bulletedList'
| 'numberedList'
| 'NumberedListWithTypes'
| 'pageBreak'
| 'link'
| 'placeholder'
| 'mjmlButton'

interface PlaceholderConfig {
  types: (string | { label: string; value: string })[]
  brackets?: { open: string; close: string }
}

interface MjmlButtonConfig {
  buttons?: ButtonItem[]
}

const basicExtensions: { format: FormatOption, extension: Extension }[] = [
  { format: 'underline', extension: Underline },
  { format: 'alignment', extension: TextAlign.configure({ types: ['heading', 'paragraph'] }) },
  { format: 'fontColor', extension: Color.configure({ types: ['textStyle'] }) },
  { format: 'fontSize', extension: FontSize.configure({ types: ['textStyle'] }) },
  { format: 'pageBreak', extension: PageBreak },
]

export const getExtensions = (
  formats: FormatOption[] = [],
  excludedFormats: FormatOption[] = [],
  placeholderConfig?: PlaceholderConfig,
  mjmlButtonConfig?: MjmlButtonConfig,
) => {
  const extensions: Extension[] = []

  const isFormatEnabled = (format: FormatOption): boolean =>
    formats.includes(format) && !excludedFormats.includes(format)

  // Core extensions
  // Disable underline in StarterKit (we add our own when enabled). Disable bulletList only when
  // we add our own configured BulletList to avoid duplicate extension names.
  extensions.push(
    StarterKit.configure({
      orderedList: isFormatEnabled('numberedList') ? false : {},
      bulletList: isFormatEnabled('bulletedList') ? false : {},
      underline: false,
      paragraph: false,
      link: false,
      trailingNode: {
        node: 'paragraph',
        notAfter: ['bulletList', 'orderedList'],
      },
    }),
    TextStyle, // Always needed for colors and font sizes
    Paragraph.extend({
      parseHTML() {
        return [{ tag: 'p' }, { tag: 'div' }]
      },
      renderHTML({ node, HTMLAttributes }) {
        const isEmpty = node.content.size === 0
        // If empty, remove style attributes (especially text-align) to prevent empty styled divs
        if (isEmpty) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { style, ...otherAttributes } = HTMLAttributes
          return ['div', otherAttributes, 0]
        }

        return ['div', HTMLAttributes, 0]
      },
    }),
  )

  basicExtensions.forEach(({ format, extension }) => {
    if (isFormatEnabled(format)) {
      extensions.push(extension)
    }
  })

  // Link extension with additional handler
  if (isFormatEnabled('link')) {
    extensions.push(
      Link.configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: false,
      }),
    )
    extensions.push(LinkBubbleMenuHandler)
  }

  // Enhanced background color extension
  if (isFormatEnabled('fontBackgroundColor')) {
    extensions.push(EnhancedHighlight.configure({
      multicolor: true,
    }))
  }

  // List extensions
  if (isFormatEnabled('bulletedList')) {
    extensions.push(BulletList.configure({
      HTMLAttributes: { style: 'list-style-type: disc;' },
    }))
  }

  // numbered list with types
  if (isFormatEnabled('numberedList')) {
    extensions.push(EnhancedOrderedList)
  }

  // Custom node extensions with configuration
  if (isFormatEnabled('placeholder') && placeholderConfig) {
    extensions.push(PlaceholderNode.configure({
      types: placeholderConfig.types,
      brackets: placeholderConfig.brackets || { open: '{{', close: '}}' },
    }))
  }

  // mjml button extension
  if (isFormatEnabled('mjmlButton') && mjmlButtonConfig) {
    extensions.push(MjmlButtonNode.configure({
      buttons: mjmlButtonConfig.buttons || [],
    }))
  }

  extensions.push(Extension.create({
    name: 'fixEmptyDivs',
    onCreate() {
      const originalGetHTML = this.editor.getHTML.bind(this.editor)
      this.editor.getHTML = () => {
        const html = originalGetHTML()
          .replace(/<div><\/div>/g, '<div><br class="ProseMirror-trailingBreak"></div>')
        return preserveWhitespaceInHtml(html)
      }
    },
  }))

  return extensions
}
